import { useEffect, useState } from 'react';
import {
	observeAuthState,
	signIn as authSignIn,
	signUp as authSignUp,
	signOutUser,
	sendPasswordReset as authSendPasswordReset,
} from '../services/auth.service';
import { auth } from '../config/firebase';
import {
	trackAuthEvent,
	trackError,
	ANALYTICS_EVENTS,
	ERROR_CONTEXTS,
} from '../services/analytics.service';

// Subscribes to Firebase auth state and exposes auth actions.
// loading is true until the first auth state callback fires.
export const useAuth = () => {
	const [user, setUser] = useState(undefined);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const unsubscribe = observeAuthState((authUser) => {
			setUser(authUser ?? null);
			setLoading(false);
		});
		return unsubscribe;
	}, []);

	const signIn = async (email, password) => {
		try {
			const result = await authSignIn(email, password);
			trackAuthEvent(ANALYTICS_EVENTS.AUTH_SIGN_IN_SUCCESS);
			return result;
		} catch (err) {
			trackError(ERROR_CONTEXTS.SIGN_IN, err);
			throw err;
		}
	};

	const signUp = async (email, password) => {
		try {
			const result = await authSignUp(email, password);
			trackAuthEvent(ANALYTICS_EVENTS.AUTH_SIGN_UP_SUCCESS);
			return result;
		} catch (err) {
			trackError(ERROR_CONTEXTS.SIGN_UP, err);
			throw err;
		}
	};

	const signOut = async () => {
		await signOutUser();
		trackAuthEvent(ANALYTICS_EVENTS.AUTH_SIGN_OUT);
	};

	const sendPasswordReset = async (email) => {
		try {
			await authSendPasswordReset(email);
			trackAuthEvent(ANALYTICS_EVENTS.AUTH_PASSWORD_RESET_SENT);
		} catch (err) {
			trackError(ERROR_CONTEXTS.SEND_PASSWORD_RESET, err);
			throw err;
		}
	};

	// auth singleton is exposed for backwards compatibility with components that
	// still pass it directly (SignIn, SignUp, PasswordReset, etc.) — remove once
	// those components are wired in p2-wire-auth-components.
	return {
		user,
		loading,
		auth,
		signIn,
		signUp,
		signOut,
		sendPasswordReset,
	};
};
